import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Bell, Globe2, MessageCircle, Save, SlidersHorizontal, UserRound } from "lucide-react";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";
import { Switch } from "../../components/ui/switch";
import { useLang } from "../../context/LangContext";
import { useTheme } from "../../context/ThemeContext";
import { useV2Session } from "../context/V2SessionContext";
import { getV2Copy } from "../content";
import {
  calculateProteinLimitFromWeight,
  REQUIRED_WEIGHT_MESSAGE,
  validateWeightKg,
} from "../lib/v2NutritionUtils";
import { syncV2NotificationSettings } from "../lib/v2ReminderRepository";
import {
  channelIncludesWhatsApp,
  maskWhatsAppNumberForDisplay,
  validateWhatsAppReminderSettings,
} from "../lib/v2WhatsAppUtils";

export default function V2Settings() {
  const { lang, setLang } = useLang();
  const { setTheme } = useTheme();
  const { isGuest, saveSettings, settings, syncProfile } = useV2Session();
  const copy = getV2Copy(lang);
  const [draft, setDraft] = useState(settings);
  const [errors, setErrors] = useState({});
  const [syncMessage, setSyncMessage] = useState("");
  const maskedWhatsAppNumber = maskWhatsAppNumberForDisplay(draft.reminders?.whatsapp_number);

  useEffect(() => {
    setDraft(settings);
  }, [settings]);

  const updateSection = (section, field, value) => {
    setDraft((current) => ({
      ...current,
      [section]: {
        ...current[section],
        [field]: value,
      },
    }));
  };

  const updateWeight = (value) => {
    const proteinLimit = calculateProteinLimitFromWeight(value);

    setErrors((current) => ({ ...current, weight_kg: "" }));
    setDraft((current) => ({
      ...current,
      profile: {
        ...current.profile,
        weight: value,
        weight_kg: value,
      },
      daily_limits: {
        ...current.daily_limits,
        protein_g: proteinLimit || current.daily_limits.protein_g,
      },
      daily_targets: {
        ...current.daily_targets,
        protein_limit_g: proteinLimit || current.daily_targets.protein_limit_g,
      },
    }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setErrors({});
    setSyncMessage("");

    const weightValidation = validateWeightKg(draft.profile.weight_kg);
    if (!weightValidation.valid) {
      setErrors({ weight_kg: weightValidation.error });
      toast.error(weightValidation.error);
      return;
    }

    const whatsappValidation = validateWhatsAppReminderSettings(draft.reminders);
    if (!whatsappValidation.valid) {
      setErrors(whatsappValidation.errors);
      toast.error("WhatsApp reminders require consent and a valid WhatsApp number.");
      return;
    }

    const proteinLimit = calculateProteinLimitFromWeight(weightValidation.weightKg);
    const saved = saveSettings({
      ...draft,
      profile: {
        ...draft.profile,
        weight: weightValidation.weightKg,
        weight_kg: weightValidation.weightKg,
      },
      daily_limits: {
        ...draft.daily_limits,
        protein_g: proteinLimit,
      },
      daily_targets: {
        ...draft.daily_targets,
        protein_limit_g: proteinLimit,
      },
      reminders: {
        ...draft.reminders,
        whatsapp_number: whatsappValidation.normalizedNumber,
      },
    });
    const profileSync = await syncProfile(saved.profile);
    if (!profileSync.ok) {
      const message = profileSync.error || "Profile loading error.";
      setSyncMessage(message);
      toast.error(message);
      return;
    }
    setLang(saved.profile.language);
    setTheme(saved.profile.theme);

    const syncResult = await syncV2NotificationSettings(saved);
    if (!syncResult.ok) {
      const message = "Saved on this device, but automatic WhatsApp scheduling could not be activated.";
      setSyncMessage(message);
      toast.warning(message);
      return;
    }

    const requestedWhatsApp =
      Boolean(saved.reminders?.whatsapp_reminders_enabled) || channelIncludesWhatsApp(saved.reminders?.channel);
    setSyncMessage(requestedWhatsApp ? "Automatic WhatsApp scheduling active." : "");
    toast.success(copy.settingsSaved);
  };

  return (
    <section className="mx-auto max-w-5xl space-y-6">
      <div className="space-y-3">
        <h1 className="text-3xl font-extrabold tracking-tight md:text-4xl">{copy.settings}</h1>
        <p className="max-w-3xl text-muted-foreground">{copy.settingsNotes}</p>
        {isGuest && (
          <p className="rounded-lg border border-border bg-card p-3 text-sm font-medium">
            {copy.guestStorageNotice}
          </p>
        )}
        {!validateWeightKg(draft.profile?.weight_kg).valid && (
          <p className="rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm font-semibold text-amber-900 dark:border-amber-900 dark:bg-amber-950 dark:text-amber-100">
            {REQUIRED_WEIGHT_MESSAGE}
          </p>
        )}
      </div>

      <form className="space-y-5" onSubmit={handleSubmit}>
        <section className="rounded-lg border border-border bg-card p-5">
          <SectionHeader icon={UserRound} title={copy.profile} />
          <div className="mt-5 grid gap-4 md:grid-cols-2">
            <Field label={copy.fullName} htmlFor="v2-full-name">
              <Input
                id="v2-full-name"
                value={draft.profile.full_name}
                onChange={(event) => updateSection("profile", "full_name", event.target.value)}
              />
            </Field>
            <Field label={copy.weight} htmlFor="v2-weight" error={errors.weight_kg}>
              <Input
                id="v2-weight"
                type="number"
                min="0"
                step="0.1"
                required
                value={draft.profile.weight_kg}
                onChange={(event) => updateWeight(event.target.value)}
              />
            </Field>
          </div>
        </section>

        <section className="rounded-lg border border-border bg-card p-5">
          <SectionHeader icon={SlidersHorizontal} title={copy.dailyLimits} />
          <p className="mt-2 text-sm text-muted-foreground">{copy.dailyLimitsSetup}</p>
          <div className="mt-5 grid gap-4 md:grid-cols-2">
            <Field label={copy.potassium} htmlFor="v2-potassium">
              <Input
                id="v2-potassium"
                type="number"
                min="0"
                value={draft.daily_limits.potassium_mg}
                onChange={(event) => updateSection("daily_limits", "potassium_mg", event.target.value)}
              />
            </Field>
            <Field label={copy.phosphorus} htmlFor="v2-phosphorus">
              <Input
                id="v2-phosphorus"
                type="number"
                min="0"
                value={draft.daily_limits.phosphorus_mg}
                onChange={(event) => updateSection("daily_limits", "phosphorus_mg", event.target.value)}
              />
            </Field>
            <Field label={copy.sodium} htmlFor="v2-sodium">
              <Input
                id="v2-sodium"
                type="number"
                min="0"
                value={draft.daily_limits.sodium_mg}
                onChange={(event) => updateSection("daily_limits", "sodium_mg", event.target.value)}
              />
            </Field>
            <Field label={copy.protein} htmlFor="v2-protein">
              <Input
                id="v2-protein"
                type="number"
                min="0"
                step="0.1"
                value={draft.daily_limits.protein_g}
                readOnly
              />
            </Field>
            <ToggleRow
              label={copy.weightFluidCard}
              checked={draft.visibility.weight_fluid_card}
              onChange={(checked) => updateSection("visibility", "weight_fluid_card", checked)}
            />
          </div>
        </section>

        <section className="rounded-lg border border-border bg-card p-5">
          <SectionHeader icon={Globe2} title={copy.preferences} />
          <div className="mt-5 grid gap-4 md:grid-cols-2">
            <Field label={copy.language} htmlFor="v2-language">
              <Select value={draft.profile.language} onValueChange={(value) => updateSection("profile", "language", value)}>
                <SelectTrigger id="v2-language">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="en">English</SelectItem>
                  <SelectItem value="ar">Arabic</SelectItem>
                </SelectContent>
              </Select>
            </Field>
            <Field label={copy.theme} htmlFor="v2-theme">
              <Select value={draft.profile.theme} onValueChange={(value) => updateSection("profile", "theme", value)}>
                <SelectTrigger id="v2-theme">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="light">{copy.light}</SelectItem>
                  <SelectItem value="dark">{copy.dark}</SelectItem>
                  <SelectItem value="system">{copy.system}</SelectItem>
                </SelectContent>
              </Select>
            </Field>
          </div>
        </section>

        <section className="rounded-lg border border-border bg-card p-5">
          <SectionHeader icon={Bell} title={copy.notifications} />
          <div className="mt-5 grid gap-4 md:grid-cols-2">
            <ToggleRow
              label={copy.enableReminders}
              checked={draft.reminders.enabled}
              onChange={(checked) => updateSection("reminders", "enabled", checked)}
            />
            <ToggleRow
              label={copy.enableNotifications}
              checked={draft.reminders.push_notifications_enabled}
              onChange={(checked) => {
                updateSection("reminders", "notifications_enabled", checked);
                updateSection("reminders", "push_notifications_enabled", checked);
              }}
            />
          </div>
        </section>

        <section className="rounded-lg border border-border bg-card p-5">
          <SectionHeader icon={MessageCircle} title={copy.whatsappSettings} />
          <p className="mt-2 text-sm text-muted-foreground">
            WhatsApp reminders require consent and a valid WhatsApp number.
          </p>
          {syncMessage && (
            <p className={getSyncMessageClass(syncMessage)}>
              {syncMessage}
            </p>
          )}
          <div className="mt-4 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
            <SettingSummary
              label="WhatsApp reminders"
              value={draft.reminders.whatsapp_reminders_enabled ? "On" : "Off"}
              tone={draft.reminders.whatsapp_reminders_enabled ? "success" : "muted"}
            />
            <SettingSummary
              label="WhatsApp number"
              value={maskedWhatsAppNumber || "Not set"}
              tone={maskedWhatsAppNumber ? "success" : "warning"}
            />
            <SettingSummary label="Reminder channel" value={getReminderChannelText(draft.reminders.channel, copy)} />
            <SettingSummary
              label="Consent"
              value={draft.reminders.whatsapp_consent ? "Accepted" : "Required"}
              tone={draft.reminders.whatsapp_consent ? "success" : "warning"}
            />
            <SettingSummary
              label="Medication name"
              value={draft.reminders.whatsapp_include_medication_name ? "Included" : "Hidden"}
            />
          </div>
          <p className="mt-4 rounded-lg border border-border bg-background p-3 text-sm text-muted-foreground">
            Privacy note: WhatsApp reminders use a generic medication reminder by default. Dose is not included, and the medication name is included only when you turn that option on.
          </p>
          <div className="mt-5 grid gap-4 md:grid-cols-2">
            <Field label={copy.whatsappNumber} htmlFor="v2-whatsapp" error={errors.whatsapp_number}>
              <Input
                id="v2-whatsapp"
                type="tel"
                value={draft.reminders.whatsapp_number}
                onChange={(event) => updateSection("reminders", "whatsapp_number", event.target.value)}
              />
            </Field>
            <Field label={copy.reminderChannel} htmlFor="v2-channel">
              <Select value={draft.reminders.channel} onValueChange={(value) => updateSection("reminders", "channel", value)}>
                <SelectTrigger id="v2-channel">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="app">{copy.appNotification}</SelectItem>
                  <SelectItem value="whatsapp">{copy.whatsapp}</SelectItem>
                  <SelectItem value="both">{copy.both}</SelectItem>
                </SelectContent>
              </Select>
            </Field>
            <ToggleRow
              label={copy.enableWhatsappReminders}
              checked={draft.reminders.whatsapp_reminders_enabled}
              onChange={(checked) => updateSection("reminders", "whatsapp_reminders_enabled", checked)}
            />
            <CheckboxRow
              label="I agree to receive medication reminder messages on WhatsApp."
              checked={draft.reminders.whatsapp_consent}
              error={errors.whatsapp_consent}
              onChange={(checked) => updateSection("reminders", "whatsapp_consent", checked)}
            />
            <ToggleRow
              label="Include medication name in WhatsApp messages"
              checked={draft.reminders.whatsapp_include_medication_name}
              onChange={(checked) => updateSection("reminders", "whatsapp_include_medication_name", checked)}
            />
          </div>
        </section>

        <div className="flex justify-end">
          <Button type="submit">
            <Save className="h-4 w-4" />
            {copy.saveSettings}
          </Button>
        </div>
      </form>
    </section>
  );
}

function getSyncMessageClass(message = "") {
  const base = "mt-4 rounded-lg border p-3 text-sm font-semibold";
  if (message === "Automatic WhatsApp scheduling active.") {
    return `${base} border-emerald-200 bg-emerald-50 text-emerald-900 dark:border-emerald-900 dark:bg-emerald-950 dark:text-emerald-100`;
  }
  return `${base} border-amber-200 bg-amber-50 text-amber-900 dark:border-amber-900 dark:bg-amber-950 dark:text-amber-100`;
}

function SectionHeader({ icon: Icon, title }) {
  return (
    <div className="flex items-center gap-3">
      <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-primary text-primary-foreground">
        <Icon className="h-4 w-4" />
      </span>
      <h2 className="text-lg font-bold">{title}</h2>
    </div>
  );
}

function Field({ label, htmlFor, error, children }) {
  return (
    <div className="space-y-2">
      <Label htmlFor={htmlFor}>{label}</Label>
      {children}
      {error && <p className="text-sm font-medium text-destructive">{error}</p>}
    </div>
  );
}

function ToggleRow({ label, checked, onChange }) {
  return (
    <div className="flex min-h-12 items-center justify-between gap-4 rounded-lg border border-border bg-background px-3 py-2">
      <span className="text-sm font-medium">{label}</span>
      <Switch checked={checked} onCheckedChange={onChange} />
    </div>
  );
}

function SettingSummary({ label, value, tone = "muted" }) {
  const toneClass =
    tone === "success"
      ? "border-emerald-200 bg-emerald-50 text-emerald-900 dark:border-emerald-900 dark:bg-emerald-950 dark:text-emerald-100"
      : tone === "warning"
        ? "border-amber-200 bg-amber-50 text-amber-900 dark:border-amber-900 dark:bg-amber-950 dark:text-amber-100"
        : "border-border bg-background text-foreground";

  return (
    <div className={`rounded-lg border px-3 py-2 text-sm ${toneClass}`}>
      <span className="block text-xs font-medium opacity-75">{label}</span>
      <span className="mt-1 block break-words font-bold">{value}</span>
    </div>
  );
}

function getReminderChannelText(channel, copy) {
  if (channel === "whatsapp") return copy.whatsapp;
  if (channel === "both") return copy.both;
  return copy.appNotification;
}

function CheckboxRow({ label, checked, error, onChange }) {
  return (
    <div className="space-y-2 rounded-lg border border-border bg-background px-3 py-2">
      <label className="flex min-h-8 items-center gap-3 text-sm font-medium">
        <input
          type="checkbox"
          className="h-4 w-4 rounded border-input"
          checked={Boolean(checked)}
          onChange={(event) => onChange(event.target.checked)}
        />
        <span>{label}</span>
      </label>
      {error && <p className="text-sm font-medium text-destructive">{error}</p>}
    </div>
  );
}
